import { Nilable } from "@shared/utils";
import { TipoModelEnum, PreguntasTipoEnum } from "@shared/enums";

export interface IBigmlFieldsDTO {
    project_id: Nilable<number>;
    train_dataset: Nilable<number>;
    predict_dataset: Nilable<number>;
    tasks: Nilable<number>;
    message: Nilable<string>;
    fields: Nilable<IBigmlFieldDTO[]>;
}

export class BigmlFieldsDTO {
    public projectId = 0;
    public trainDataset = 0;
    public predictDataset = 0;
    public tasks = 0;
    public message = "";
    public fields: Nilable<BigmlFieldDTO[]> = null;

    constructor(json: Nilable<Partial<IBigmlFieldsDTO>> = null) {
        if (json != null) {
            this.cargarDTO(json);
        }
    }

    public cargarDTO(json: Partial<IBigmlFieldsDTO>) {
        if (json.project_id != null) {
            this.projectId = json.project_id;
        }
        if (json.train_dataset != null) {
            this.trainDataset = json.train_dataset;
        }
        if (json.predict_dataset != null) {
            this.predictDataset = json.predict_dataset;
        }
        if (json.tasks != null) {
            this.tasks = json.tasks;
        }
        if (json.message != null) {
            this.message = json.message;
        }
        if (json.fields != null) {
            this.fields = json.fields.map(field => new BigmlFieldDTO(field));
        }
    }

    public toJSON(): IBigmlFieldsDTO {
        return {
            project_id: this.projectId,
            train_dataset: this.trainDataset,
            predict_dataset: this.predictDataset,
            message: this.message,
            tasks: this.tasks,
            fields: this.fields?.map(field => field.toJSON()) ?? null
        };
    }
}

export interface IBigmlFieldDTO {
    id: number;
    model_type: Nilable<number>;
    prediction_dataset_resource: Nilable<IBigmlPredictResourceDTO>;
    name: Nilable<string>;
    field: Nilable<string>;
    field_pred: Nilable<string>;
    train: Nilable<number>;
    model: Nilable<number>;
    project_id: Nilable<number>;
    summary: Nilable<string>;
    status: Nilable<string>;
    classes: Nilable<string[]>;
    compare_predicts: Nilable<boolean>;
    desired_class: Nilable<string>;
    desired_class_percentage: Nilable<number>;
    category_type: Nilable<string>;
    industry_type: Nilable<string>;
    text_model: Nilable<string>;
    additional_items: Nilable<number[]>;
    less_frequent_text_answers: Nilable<IBigmlFieldTextAnswerDTO[]>;
}

export class BigmlFieldDTO {
    public id = 0;
    public modelType = TipoModelEnum.ENSEMBLE;
    public predictionDatasetResource: Nilable<BigmlPredictResourceDTO> = null; // se usa, pero literalmente ni sirve.
    public name = "";
    public field = "";
    public fieldPred = "";
    public train = 0;
    public type: Nilable<PreguntasTipoEnum> = null;
    public isTextField = false;
    public categoryType: Nilable<string> = null;
    public industryType: Nilable<string> = null;
    public textModel: Nilable<string> = null;
    public additionalItems: Nilable<number[]> = [];
    public lessFrequentTextAnswers: BigmlFieldTextAnswerDTO[] = [];
    public modelId = 0;
    public projectId = 0;
    public summary = "";
    public status = "";
    public borrarVacios = false;
    public classes: Nilable<string[]> = [];
    public comparePredicts = false;
    public desiredClass = "";
    public desiredClassPercentage = 0;

    constructor(json: Nilable<Partial<IBigmlFieldDTO>> = null) {
        if (json != null) {
            this.cargarDTO(json);
        }
    }

    public cargarDTO(json: Partial<IBigmlFieldDTO>) {
        if (json.id != null) {
            this.id = json.id;
        }
        if (json.model_type != null) {
            this.modelType = json.model_type;
        }

        this.predictionDatasetResource = new BigmlPredictResourceDTO(json.prediction_dataset_resource);

        if (json.name != null) {
            this.name = json.name;
        }
        if (json.field != null) {
            this.field = json.field;
        }
        if (json.field_pred != null) {
            this.fieldPred = json.field_pred;
        }
        if (json.train != null) {
            this.train = json.train;
        }
        this.modelId = json.model || 0;
        if (json.project_id != null) {
            this.projectId = json.project_id;
        }
        if (json.summary != null) {
            this.summary = json.summary;
        }
        if (json.status != null) {
            this.status = json.status;
        }
        if (json.compare_predicts != null) {
            this.comparePredicts = json.compare_predicts;
        }
        if (json.desired_class != null) {
            this.desiredClass = json.desired_class;
        }
        if (json.desired_class_percentage != null) {
            this.desiredClassPercentage = json.desired_class_percentage;
        }
        if (json.less_frequent_text_answers != null) {
            this.lessFrequentTextAnswers = json.less_frequent_text_answers.map(
                lessFrequentAnswer => new BigmlFieldTextAnswerDTO(lessFrequentAnswer)
            );
        }

    }

    public toJSON(): IBigmlFieldDTO {
        return {
            id: this.id,
            model_type: this.modelType,
            prediction_dataset_resource: this.predictionDatasetResource?.toJSON() ?? null,
            name: this.name,
            field: this.field,
            field_pred: this.fieldPred,
            train: this.train,
            model: this.modelId,
            project_id: this.projectId,
            summary: this.summary,
            status: this.status,
            classes: this.classes,
            compare_predicts: this.comparePredicts,
            desired_class: this.desiredClass,
            desired_class_percentage: this.desiredClassPercentage,
            category_type: this.categoryType,
            industry_type: this.industryType,
            text_model: this.textModel,
            additional_items: this.additionalItems,
            less_frequent_text_answers: this.lessFrequentTextAnswers?.map(
                lessFrequentAnswer => lessFrequentAnswer.toJSON()
            )
        };
    }
}

export interface IBigmlFieldTextAnswerDTO {
    class_name: string;
    length: number;
}

export class BigmlFieldTextAnswerDTO {
    public answerText = "";
    public longitud = 0;

    constructor(json: Nilable<Partial<IBigmlFieldTextAnswerDTO>> = null) {
        if (json != null) {
            this.cargarDTO(json);
        }
    }

    public cargarDTO(json: Partial<IBigmlFieldTextAnswerDTO>) {
        if (json.class_name != null) {
            this.answerText = json.class_name;
        }
        if (json.length != null) {
            this.longitud = json.length;
        }
    }

    public toJSON(): IBigmlFieldTextAnswerDTO {
        return {
            class_name: this.answerText,
            length: this.longitud
        };
    }
}

export interface IBigmlPredictResourceDTO {
    dataset: string;
}

export class BigmlPredictResourceDTO {
    public dataset = "";

    constructor(json: Nilable<Partial<IBigmlPredictResourceDTO>> = null) {
        if (json != null) {
            this.cargarDTO(json);
        }
    }

    public cargarDTO(json: Partial<IBigmlPredictResourceDTO>) {
        if (json.dataset != null) {
            this.dataset = json.dataset;
        }
    }

    public toJSON(): IBigmlPredictResourceDTO {
        return {
            dataset: this.dataset
        };
    }
}
